<template>
  <div class="hps-Wrapper">
    <div class="hps-Wrapper_Nav">
      <AppRadioGroup
        v-if="areFiltersValuesResolved"
        v-model="filterBy"
        :data-auto-testid="SIDEBAR_FILTER_BY_SWITCH_TEST_ID"
        :data-testid="SIDEBAR_FILTER_BY_SWITCH_TEST_ID"
        :options="FILTER_KINDS"
        align-width
        full-width
        name="filter-by-switch"
        style="--option-padding: 0 8px 0 4px"
      >
        <template #item-label="{ item }">
          <AppRadioGroupNextOptionWithIcon :option="item" centered truncated />
        </template>
      </AppRadioGroup>
      <SkeletonItem v-else width="100%" />

      <AppDivider class="hps-Divider" no-margin />
    </div>

    <template v-if="areFiltersValuesResolved">
      <HomepageGroupsFilter
        v-if="filterBy === FILTER_BY.GROUPS"
        v-model="homePageFilters[NEW_HP_FILTERS_KEYS.SELECTED_GROUP]"
        :data-auto-testid="SIDEBAR_GROUPS_FILTER_TEST_ID"
        :data-testid="SIDEBAR_GROUPS_FILTER_TEST_ID"
      />
      <HomepageUsersFilter
        v-if="filterBy === FILTER_BY.USERS"
        v-model="homePageFilters[NEW_HP_FILTERS_KEYS.SELECTED_USER]"
        :data-auto-testid="SIDEBAR_USERS_FILTER_TEST_ID"
        :data-testid="SIDEBAR_USERS_FILTER_TEST_ID"
      />
    </template>

    <div v-else class="hps-Loader">
      <SkeletonItem width="100%" />
      <SkeletonItem :size="SKELETON_SIZES.XS" class="hps-Loader_Title" width="60px" />

      <HomepageGroupsFilterLoader />
    </div>
  </div>
</template>

<script setup>
import { isNull } from 'lodash'
import { inject, ref, watch } from 'vue'

import { SKELETON_SIZES } from '@/utils/components-configurations/skeleton-item'
import { HOMEPAGE_SIDEBAR_LIST_ITEM_GAP, HOMEPAGE_SIDEBAR_LIST_ITEM_HEIGHT } from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { NEW_HP_FILTERS_KEYS } from '@/utils/okr-elements/filters'

import HomepageGroupsFilter from '@/components/home-page/HomepageGroupsFilter'
import HomepageGroupsFilterLoader from '@/components/home-page/HomepageGroupsFilterLoader'
import HomepageUsersFilter from '@/components/home-page/HomepageUsersFilter'
import {
  SIDEBAR_FILTER_BY_SWITCH_TEST_ID,
  SIDEBAR_GROUPS_FILTER_TEST_ID,
  SIDEBAR_USERS_FILTER_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppDivider from '@/components/ui/AppDivider'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import AppRadioGroupNextOptionWithIcon from '@/components/ui/AppRadioGroup/AppRadioGroupNextOptionWithIcon'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'

defineOptions({
  name: 'HomepageSidebar'
})

const FILTER_BY = {
  USERS: 'users',
  GROUPS: 'groups'
}

const FILTER_KINDS = [
  {
    label: 'field.groups.title',
    value: FILTER_BY.GROUPS,
    icon: 'team-next'
  },

  {
    label: 'common.users',
    value: FILTER_BY.USERS,
    icon: 'user-next'
  }
]

const homePageState = inject(homePageStateInjectionKey)
const homePageFilters = homePageState.filterValues
const areFiltersValuesResolved = homePageState.areFiltersValuesResolved

const filterBy = ref(null)

watch(
  homePageState.areFiltersValuesResolved,
  areResolved => {
    if (!areResolved) {
      return
    }

    const isUserSelected = !isNull(homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER])

    filterBy.value = isUserSelected ? FILTER_BY.USERS : FILTER_BY.GROUPS
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.hps-Wrapper {
  z-index: 1;
  width: 240px;
  min-width: 240px;
  flex-shrink: 0;
  height: 100%;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  --ob-hps-mx: 20px;
  --ob-hpgf-item-height: calc(v-bind(HOMEPAGE_SIDEBAR_LIST_ITEM_HEIGHT) * 1px);
  --ob-hpgf-item-gap: calc(v-bind(HOMEPAGE_SIDEBAR_LIST_ITEM_GAP) * 1px);

  box-shadow: 8px 0 19px 0 rgba($dark-1, 0.13);
}

.hps-Divider {
  margin-block: 24px var(--ob-hps-mx);
}

.hps-Wrapper_Nav {
  padding-inline: var(--ob-hps-mx);
}

.hps-Loader {
  padding-inline: var(--ob-hps-mx);
  display: flex;
  flex-direction: column;
  gap: var(--ob-hpgf-item-gap);
}

.hps-Loader_Title {
  margin-top: 14px;
}
</style>
